import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getTenantCode, withTenant } from '../tenant';
import { WizardPreviewModal } from './WizardPreviewModal';
import { ReloadOutlined, FileTextOutlined, SendOutlined } from '@ant-design/icons';

const supportedLanguages = [
  { code: 'it', name: 'Italiano', message: 'Puoi fare domande e avere risposte in italiano' },
  { code: 'gb', name: 'English', message: 'You can ask questions and get answers in English' },
  { code: 'fr', name: 'Français', message: 'Vous pouvez poser des questions et obtenir des réponses en français' },
  { code: 'de', name: 'Deutsch', message: 'Sie können Fragen stellen und Antworten auf Deutsch erhalten' },
  { code: 'es', name: 'Español', message: 'Puedes hacer preguntas y obtener respuestas en español' },
  { code: 'pt', name: 'Português', message: 'Você pode fazer perguntas e obter respostas em português' },
  { code: 'cn', name: '中文', message: '您可以用中文提问并获得回答' },
  { code: 'jp', name: '日本語', message: '日本語で質問して回答を得ることができます' },
  { code: 'sa', name: 'العربية', message: 'يمكنك طرح الأسئلة والحصول على إجابات باللغة العربية' },
  { code: 'ru', name: 'Русский', message: 'Вы можете задавать вопросы и получать ответы на русском языке' },
  { code: 'kr', name: '한국어', message: '한국어로 질문하고 답변을 받을 수 있습니다' },
];

function LanguageFlags() {
  const [activeTooltip, setActiveTooltip] = useState(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (activeTooltip !== null) {
      const timer = setTimeout(() => setActiveTooltip(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [activeTooltip]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target)) {
        setActiveTooltip(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const handleFlagClick = (index) => {
    setActiveTooltip(activeTooltip === index ? null : index);
  };

  return (
    <div className="supported-languages" ref={tooltipRef}>
      {supportedLanguages.map((lang, index) => (
        <div key={lang.code} className="flag-wrapper">
          <img
            className="flag-icon"
            src={`https://flagcdn.com/w40/${lang.code}.png`}
            alt={lang.name}
            title={lang.message}
            onClick={() => handleFlagClick(index)}
            onTouchEnd={(e) => { e.preventDefault(); handleFlagClick(index); }}
          />
          {activeTooltip === index && (
            <div className="flag-tooltip">
              {lang.message}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

const createId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2, 10);
};

const initialMessages = [
  {
    id: createId(),
    role: 'assistant',
    content:
      'Ciao! Fammi una domanda sui documenti caricati oppure chiedimi qualcosa di generico.',
    sources: [],
  },
];

const API_ENDPOINT = '/api/query';

const toSourceFileName = (source) => {
  if (!source) return '';
  const raw = String(source).trim();
  if (!raw) return '';
  const noQuery = raw.split('?')[0];
  let decoded = noQuery;
  try {
    decoded = decodeURIComponent(noQuery);
  } catch {
    // keep original if decoding fails
  }
  const parts = decoded.split(/[/\\]/);
  return parts[parts.length - 1] || decoded;
};

export function ChatPage({ brand }) {
  const [messages, setMessages] = useState(initialMessages);
  const [question, setQuestion] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [consentLoadingId, setConsentLoadingId] = useState(null);
  const [error, setError] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [fileListError, setFileListError] = useState('');
  const [fileListLoading, setFileListLoading] = useState(false);
  const [wizardData, setWizardData] = useState({ captions: [], imageUrls: [] });
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardChecked, setWizardChecked] = useState(false);
  const [tenantLabel, setTenantLabel] = useState('');
  const [tenantLogo, setTenantLogo] = useState('');
  const [tenantTone, setTenantTone] = useState('neutrale');

  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  const buildContext = useCallback((history) => {
    const recent = history
      .filter(m => m.role === 'user' || m.role === 'assistant')
      .slice(-8);

    const lines = recent.flatMap((m) => {
      const messageLine = `${m.role}: ${m.content}`.slice(0, 500);

      if (m.role !== 'assistant' || !Array.isArray(m.sources) || m.sources.length === 0) {
        return [messageLine];
      }

      const sourceNames = m.sources
        .map(toSourceFileName)
        .filter(Boolean)
        .slice(0, 8);

      if (sourceNames.length === 0) {
        return [messageLine];
      }

      return [messageLine, `sources: ${sourceNames.join(', ')}`];
    });

    const ctx = lines.join('\n').slice(0, 2500);
    return ctx;
  }, []);

  const resetConversation = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setMessages(initialMessages);
    setQuestion('');
    setError(null);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const loadFiles = useCallback(async () => {
    setFileListLoading(true);
    setFileListError('');
    try {
      const res = await fetch(withTenant('/api/files/list?limit=200'), { method: 'GET' });
      if (!res.ok) {
        const problem = await res.json().catch(() => ({}));
        throw new Error(problem.detail || 'Impossibile caricare la lista file.');
      }
      const data = await res.json();
      setFileList(Array.isArray(data) ? data : []);
    } catch (err) {
      setFileListError(err instanceof Error ? err.message : String(err));
    } finally {
      setFileListLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      const trimmed = question.trim();

      if (!trimmed || isLoading) {
        return;
      }

      const normalized = trimmed.toLowerCase();
      if (normalized === 'nuova ricerca' || normalized === '/reset') {
        resetConversation();
        return;
      }

      const contextToSend = buildContext(messages);

      const userMessage = {
        id: createId(),
        role: 'user',
        content: trimmed,
        sources: [],
      };

      setMessages((prev) => [...prev, userMessage]);
      setQuestion('');
      setIsLoading(true);
      setError(null);

      if (abortRef.current) {
        abortRef.current.abort();
      }
      abortRef.current = new AbortController();

      try {
        const response = await fetch(withTenant(API_ENDPOINT), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            question: trimmed,
            maxRows: 30,
            context: contextToSend,
            tone: tenantTone,
            generalFallbackConsent: null,
          }),
          signal: abortRef.current.signal,
        });

        if (!response.ok) {
          const problem = await response.json().catch(() => ({}));
          throw new Error(problem.detail || 'Richiesta non riuscita');
        }

        const payload = await response.json();
        const answerText = payload.answer?.trim() || 'Nessuna risposta disponibile.';
        const assistantMessage = {
          id: createId(),
          role: 'assistant',
          content:
            payload.awaitingGeneralFallbackConsent && payload.generalFallbackPrompt
              ? `${answerText}\n\n${payload.generalFallbackPrompt}`
              : answerText,
          sources: payload.sources ?? [],
          mode: payload.mode,
          analysisSteps: payload.analysisSteps ?? null,
          awaitingGeneralFallbackConsent: !!payload.awaitingGeneralFallbackConsent,
          usedGeneralFallback: !!payload.usedGeneralFallback,
          fallbackQuestion: payload.awaitingGeneralFallbackConsent ? trimmed : null,
          fallbackContext: payload.awaitingGeneralFallbackConsent ? contextToSend : null,
        };

        setMessages((prev) => [...prev, assistantMessage]);

        // Log the conversation
        try {
          await fetch(withTenant('/api/logs'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ question: trimmed, answer: answerText }),
          });
        } catch {
          // Ignore logging errors
        }
      } catch (err) {
        const detail = err instanceof Error ? err.message : String(err);
        const fallbackMessage = {
          id: createId(),
          role: 'assistant',
          content: `Si è verificato un errore durante l’elaborazione della richiesta: ${detail}`,
          sources: [],
        };
        setMessages((prev) => [...prev, fallbackMessage]);
        setError(detail);
      } finally {
        setIsLoading(false);
      }
    },
    [question, isLoading, buildContext, messages, resetConversation, tenantTone],
  );

  const handleGeneralFallbackConsent = useCallback(
    async (assistantMessage, consent) => {
      if (isLoading || !assistantMessage?.fallbackQuestion || !assistantMessage?.fallbackContext) return;

      const consentLabel = consent === 'yes' ? 'Sì, fai la ricerca generale' : 'No, resta sulla KB';
      const userMessage = {
        id: createId(),
        role: 'user',
        content: consentLabel,
        sources: [],
      };
      if (consent === 'no') {
        const nextAssistantMessage = {
          id: createId(),
          role: 'assistant',
          content:
            'Ok, resto solo sui documenti della knowledge base. Non ho trovato questa informazione nei documenti disponibili.',
          sources: assistantMessage.sources ?? [],
          mode: 'database',
          analysisSteps: assistantMessage.analysisSteps ?? null,
          awaitingGeneralFallbackConsent: false,
          usedGeneralFallback: false,
          fallbackQuestion: null,
          fallbackContext: null,
        };
        setMessages((prev) => [...prev, userMessage, nextAssistantMessage]);
        setError(null);
        return;
      }

      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);
      setConsentLoadingId(assistantMessage.id);
      setError(null);

      if (abortRef.current) {
        abortRef.current.abort();
      }
      abortRef.current = new AbortController();

      try {
        const response = await fetch(withTenant(API_ENDPOINT), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            question: assistantMessage.fallbackQuestion,
            maxRows: 30,
            context: assistantMessage.fallbackContext,
            tone: tenantTone,
            generalFallbackConsent: consent,
          }),
          signal: abortRef.current.signal,
        });

        if (!response.ok) {
          const problem = await response.json().catch(() => ({}));
          throw new Error(problem.detail || 'Richiesta non riuscita');
        }

        const payload = await response.json();
        const answerText = payload.answer?.trim() || 'Nessuna risposta disponibile.';
        const nextAssistantMessage = {
          id: createId(),
          role: 'assistant',
          content:
            payload.awaitingGeneralFallbackConsent && payload.generalFallbackPrompt
              ? `${answerText}\n\n${payload.generalFallbackPrompt}`
              : answerText,
          sources: payload.sources ?? [],
          mode: payload.mode,
          analysisSteps: payload.analysisSteps ?? null,
          awaitingGeneralFallbackConsent: !!payload.awaitingGeneralFallbackConsent,
          usedGeneralFallback: !!payload.usedGeneralFallback,
          fallbackQuestion: payload.awaitingGeneralFallbackConsent ? assistantMessage.fallbackQuestion : null,
          fallbackContext: payload.awaitingGeneralFallbackConsent ? assistantMessage.fallbackContext : null,
        };

        setMessages((prev) => [...prev, nextAssistantMessage]);
      } catch (err) {
        const detail = err instanceof Error ? err.message : String(err);
        const fallbackMessage = {
          id: createId(),
          role: 'assistant',
          content: `Si è verificato un errore durante l’elaborazione della richiesta: ${detail}`,
          sources: [],
        };
        setMessages((prev) => [...prev, fallbackMessage]);
        setError(detail);
      } finally {
        setIsLoading(false);
        setConsentLoadingId(null);
      }
    },
    [isLoading, tenantTone],
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        handleSubmit(event);
      }
    },
    [handleSubmit],
  );

  const stopRequest = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
      setIsLoading(false);
    }
  }, []);

  const tenantCode = getTenantCode();

  useEffect(() => {
    if (brand?.displayName || brand?.logoBase64 || brand?.logoUrl || brand?.responseTone) {
      setTenantLabel(brand?.displayName || '');
      setTenantLogo(brand?.logoBase64 || brand?.logoUrl || '');
      setTenantTone(brand?.responseTone || 'neutrale');
      return;
    }
    const loadTenantLabel = async () => {
      if (!tenantCode) return;
      try {
        const res = await fetch('/api/admin/tenants', { method: 'GET' });
        if (!res.ok) return;
        const tenants = await res.json().catch(() => []);
        if (!Array.isArray(tenants)) return;
        const match = tenants.find((t) => String(t.id).toLowerCase() === String(tenantCode).toLowerCase());
        if (!match) return;
        setTenantLabel(match.displayName || match.name || '');
        setTenantLogo(match.logoBase64 || match.logoUrl || '');
        setTenantTone(match.responseTone || 'neutrale');
      } catch {
        // ignore
      }
    };
    loadTenantLabel();
  }, [brand, tenantCode]);

  const getCookie = useCallback((name) => {
    if (typeof document === 'undefined') return '';
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return '';
  }, []);

  const setCookie = useCallback((name, value, days = 365) => {
    if (typeof document === 'undefined') return;
    const expires = new Date(Date.now() + days * 86400000).toUTCString();
    document.cookie = `${name}=${value}; expires=${expires}; path=/`;
  }, []);

  const isWizardComplete = useCallback((data) => {
    const captions = data?.captions || [];
    const images = data?.imageUrls || [];
    if (captions.length < 5 || images.length < 5) return false;
    return captions.every((c) => String(c || '').trim()) && images.every((u) => String(u || '').trim());
  }, []);

  useEffect(() => {
    const checkWizard = async () => {
      if (!tenantCode) return;
      const cookieKey = `wizard_hide_${tenantCode}`;
      if (getCookie(cookieKey) === '1') return;
      try {
        const res = await fetch(withTenant('/api/admin/wizard'), { method: 'GET' });
        if (!res.ok) return;
        const data = await res.json();
        if (!isWizardComplete(data)) return;
        setWizardData({
          captions: Array.isArray(data?.captions) ? data.captions : [],
          imageUrls: Array.isArray(data?.imageUrls) ? data.imageUrls : [],
        });
        setWizardOpen(true);
      } finally {
        setWizardChecked(true);
      }
    };
    if (!wizardChecked) {
      checkWizard();
    }
  }, [tenantCode, getCookie, isWizardComplete, wizardChecked]);

  const buildStaticFileUrl = useCallback(
    (source) => {
      if (!source) return '';
      if (source.startsWith('/uploads/')) return source;
      const fileName = source.split(/[/\\]/).pop() || source;
      if (!tenantCode) return '';
      return `/uploads/${tenantCode}/${encodeURIComponent(fileName)}`;
    },
    [tenantCode],
  );

  const messageItems = useMemo(
    () =>
      messages.map((message) => (
        <ChatBubble
          key={message.id}
          message={message}
          buildStaticFileUrl={buildStaticFileUrl}
          onGeneralFallbackConsent={handleGeneralFallbackConsent}
          consentLoading={consentLoadingId === message.id}
          isGlobalLoading={isLoading}
        />
      )),
    [messages, buildStaticFileUrl, handleGeneralFallbackConsent, consentLoadingId, isLoading],
  );

  return (
    <div className="chat-container">
      <header className="chat-header">
        <div className="header-brand">
          {brand?.logoBase64 || brand?.logoUrl ? (
            <img
              className="brand-logo"
              src={brand.logoBase64 || brand.logoUrl}
              alt={brand.displayName || 'Logo'}
            />
          ) : (
            <svg className="app-logo" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
              <rect width="100" height="100" rx="20" fill="#0066B3"/>
              <text x="50" y="70" fontSize="80" fontWeight="bold" fill="white" textAnchor="middle" fontFamily="Arial, sans-serif">Q</text>
            </svg>
          )}
          <div>
            <h1>{brand?.displayName || ''}</h1>
            <p>Sportello Informativo basato su IA</p>
          </div>
        </div>
        <div className="chat-header-controls">
          <LanguageFlags />
          {isLoading && (
            <button className="secondary-button" onClick={stopRequest}>
              Annulla
            </button>
          )}
        </div>
      </header>

      <main className="chat-content" ref={scrollRef}>
        {messageItems}
        {isLoading && <TypingIndicator />}
      </main>

      <footer className="chat-input-area">
        {error && <div className="error-banner">{error}</div>}
        <div className="chat-input-grid">
          <form className="chat-form chat-form--bar" onSubmit={handleSubmit}>
            <div className="chat-input-bar">
              <textarea
                className="chat-input-field"
                rows={1}
                placeholder="Scrivi un messaggio..."
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
                aria-label="Messaggio"
              />
              <button type="submit" className="chat-send-btn" disabled={isLoading || !question.trim()} title="Invia">
                {isLoading ? <span className="chat-send-label">...</span> : <><SendOutlined className="chat-send-icon" /><span className="chat-send-label">Invia</span></>}
              </button>
            </div>
          </form>
          <aside className="chat-files-panel">
            <div className="chat-files-header">
              <strong>File caricati</strong>
              <button className="secondary-button" type="button" onClick={loadFiles} disabled={fileListLoading}>
                <ReloadOutlined />
              </button>
            </div>
            {fileListError && <div className="error-banner">{fileListError}</div>}
            <div className="chat-files-list">
              {fileListLoading && <span className="muted">Caricamento...</span>}
              {!fileListLoading && fileList.length === 0 && <span className="muted">Nessun file.</span>}
              {fileList.map((item) => (
                <a
                  key={item.filePath || item.sourceFile}
                  className="chat-file-item"
                  href={item.fileUrl || buildStaticFileUrl(item.filePath || item.sourceFile || '')}
                  target="_blank"
                  rel="noreferrer"
                >
                  {item.sourceFile || item.filePath}
                </a>
              ))}
            </div>
          </aside>
        </div>
      </footer>
      <WizardPreviewModal
        open={wizardOpen}
        imageUrls={wizardData.imageUrls || []}
        captions={wizardData.captions || []}
        tenantName={tenantLabel}
        tenantLogo={tenantLogo}
        onClose={() => setWizardOpen(false)}
        onDontShowAgain={() => {
          if (tenantCode) {
            setCookie(`wizard_hide_${tenantCode}`, '1', 365);
          }
        }}
      />
    </div>
  );
}

function ChatBubble({ message, buildStaticFileUrl, onGeneralFallbackConsent, consentLoading, isGlobalLoading }) {
  const [toast, setToast] = useState(null);
  const isUser = message.role === 'user';
  const bubbleClass = ['chat-bubble', isUser ? 'user' : 'assistant']
    .filter(Boolean)
    .join(' ');

  const renderBold = (text) => {
    const escapeHtml = (s) =>
      s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    const escaped = escapeHtml(text ?? '');
    // convert **bold** to <strong>bold</strong>, non-greedy
    const withBold = escaped.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    // preserve line breaks
    return withBold.replace(/\n/g, '<br/>');
  };


  const baseName = (p) => {
    if (!p) return '';
    const parts = p.split(/[/\\]/);
    return parts[parts.length - 1] || p;
  };

  const resolveSourceUrl = (source) => {
    if (!source) return '';
    const staticUrl = buildStaticFileUrl?.(source);
    return staticUrl || withTenant(`/api/files?path=${encodeURIComponent(source)}`);
  };

  return (
    <article className={bubbleClass}>
      {message.analysisSteps && message.analysisSteps.length > 0 && (
        <details className="analysis-steps">
          <summary>🔍 Processo di analisi ({message.analysisSteps.length} step)</summary>
          <ol>
            {message.analysisSteps.map((step, idx) => (
              <li key={idx}>{step}</li>
            ))}
          </ol>
        </details>
      )}
      <div className="bubble-body">
        <p dangerouslySetInnerHTML={{ __html: renderBold(message.content) }} />
        {message.mode && (
          <span className="mode-chip">
            {message.mode === 'database' ? 'DB' : 'LLM'}
          </span>
        )}
        {message.usedGeneralFallback && (
          <span className="mode-chip" style={{ marginLeft: 8 }}>
            General one-shot
          </span>
        )}
      </div>
      {message.awaitingGeneralFallbackConsent && message.fallbackQuestion && (
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button
            type="button"
            className="secondary-button"
            disabled={consentLoading || isGlobalLoading}
            onClick={() => onGeneralFallbackConsent?.(message, 'yes')}
          >
            {consentLoading ? 'Attendere...' : 'Sì'}
          </button>
          <button
            type="button"
            className="secondary-button"
            disabled={consentLoading || isGlobalLoading}
            onClick={() => onGeneralFallbackConsent?.(message, 'no')}
          >
            No
          </button>
        </div>
      )}
      {message.sources?.length > 0 && (
        <ul className="sources-list">
          {message.sources.map((source) => (
            <li key={source}>
              <a
                className="source-link"
                href={resolveSourceUrl(source)}
                target="_blank"
                rel="noreferrer"
                title="Apri il file in una nuova scheda"
              >
                <FileTextOutlined /> {baseName(source)}
              </a>
            </li>
          ))}
        </ul>
      )}
      {toast && <div className="toast-message">{toast}</div>}
    </article>
  );
}

function TypingIndicator() {
  return (
    <div className="typing-indicator">
      <span />
      <span />
      <span />
    </div>
  );
}

